import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Copy, Gift, Share2, Users, Sparkles } from "lucide-react";

type ReferralsResponse = {
  code: string | null;
  link: string | null;
  rewardCredits: number;
  creditsEarned: number;
  referrals: Array<{
    id: string;
    createdAt: string | null;
    tier: string | null;
    paid: boolean;
    rewarded: boolean;
  }>;
};

export default function ReferralsPage() {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const { data, isLoading } = useQuery<ReferralsResponse>({
    queryKey: ["/api/me/referrals"],
  });

  const copyLink = async () => {
    if (!data?.link) return;
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      toast({ title: "Link copied", description: "Share it with your friends." });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Couldn't copy", description: "Copy the link manually.", variant: "destructive" });
    }
  };

  const share = async () => {
    if (!data?.link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Try Tork – Digital Domination",
          text: "I use Tork to dominate local search. Sign up with my link and we both get bonus scan credits.",
          url: data.link,
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  const totalReferrals = data?.referrals.length || 0;
  const paidReferrals = data?.referrals.filter((r) => r.paid).length || 0;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="page-referrals">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="text-page-title">
          <Gift className="h-7 w-7 text-primary" /> Refer Friends, Earn Credits
        </h1>
        <p className="text-muted-foreground mt-2">
          Share your link. When a friend signs up and upgrades to a paid plan,
          you BOTH get {data?.rewardCredits || 500} bonus GeoGrid scan credits.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your referral link</CardTitle>
          <CardDescription>Send this to anyone — they'll get a bonus too.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : data?.link ? (
            <>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={data.link}
                  className="font-mono text-sm"
                  data-testid="input-referral-link"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button onClick={copyLink} variant="secondary" data-testid="button-copy-link">
                  <Copy className="h-4 w-4 mr-2" />
                  {copied ? "Copied!" : "Copy"}
                </Button>
                <Button onClick={share} data-testid="button-share-link">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
              <div className="text-sm text-muted-foreground">
                Your code: <span className="font-mono font-semibold text-foreground">{data.code}</span>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground">Referral link not yet available — try refreshing.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Total signups
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-total-referrals">{totalReferrals}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Converted to paid
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-paid-referrals">{paidReferrals}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Gift className="h-4 w-4" /> Credits earned
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-credits-earned">
              {data?.creditsEarned || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your referrals</CardTitle>
          <CardDescription>Anyone who signed up through your link.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : !data?.referrals.length ? (
            <p className="text-center text-muted-foreground py-8" data-testid="text-empty-referrals">
              No referrals yet. Share your link to get started.
            </p>
          ) : (
            <div className="divide-y">
              {data.referrals.map((r, i) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between py-3"
                  data-testid={`row-referral-${i}`}
                >
                  <div>
                    <div className="font-medium">Friend #{i + 1}</div>
                    <div className="text-xs text-muted-foreground">
                      Joined {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.paid ? (
                      <Badge variant="default" className="capitalize">{r.tier} plan</Badge>
                    ) : (
                      <Badge variant="secondary">Free</Badge>
                    )}
                    {r.rewarded && (
                      <Badge variant="outline" className="gap-1">
                        <Gift className="h-3 w-3" /> Rewarded
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
